/**
 * memberLevelStore.ts
 * Global dictionary for member levels.
 * SystemSettings manages this, all other components read from it.
 */

export interface MemberLevel {
  id: string;
  name: string;
  description: string;
  className: string;   // Tailwind badge classes e.g. "bg-red-50 text-red-600"
  minPoints: number;
  sortOrder: number;
  isSystem?: boolean;  // System levels cannot be deleted
  benefitIds?: string[];
  annualFee?: number;
  /** 与 ageMaxInclusive 同时有值时表示入会年龄限制（周岁，含） */
  ageMinInclusive?: number | null;
  ageMaxInclusive?: number | null;
  /** 与 GET /member-levels 的 isEnabled 一致；缺省视为 true */
  isEnabled?: boolean;
}

const COLOR_PRESETS: { label: string; className: string }[] = [
  { label: "红色", className: "bg-red-50 text-red-600" },
  { label: "橙色", className: "bg-orange-50 text-orange-600" },
  { label: "金色", className: "bg-amber-50 text-amber-600" },
  { label: "绿色", className: "bg-green-50 text-green-600" },
  { label: "青色", className: "bg-teal-50 text-teal-600" },
  { label: "玫红", className: "bg-pink-50 text-pink-600" },
  { label: "灰色", className: "bg-gray-100 text-gray-600" },
];

export { COLOR_PRESETS };

const _levels: MemberLevel[] = [];

export const memberLevelStore = {
  getAll: (): MemberLevel[] => [..._levels].sort((a, b) => a.sortOrder - b.sortOrder),

  getNames: (): string[] =>
    [..._levels].sort((a, b) => a.sortOrder - b.sortOrder).map((l) => l.name),

  getClassName: (name: string): string =>
    _levels.find((l) => l.name === name)?.className ?? "bg-gray-100 text-gray-600",

  add: (item: Omit<MemberLevel, "id" | "sortOrder">) => {
    const maxOrder = Math.max(..._levels.map((l) => l.sortOrder), 0);
    _levels.push({ ...item, id: `l${Date.now()}`, sortOrder: maxOrder + 1 });
  },

  update: (id: string, changes: Partial<MemberLevel>) => {
    const idx = _levels.findIndex((l) => l.id === id);
    if (idx !== -1) Object.assign(_levels[idx], changes);
  },

  remove: (id: string) => {
    const idx = _levels.findIndex((l) => l.id === id && !l.isSystem);
    if (idx !== -1) _levels.splice(idx, 1);
  },

  moveUp: (id: string) => {
    const sorted = [..._levels].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((l) => l.id === id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    const curr = sorted[idx];
    const tmp = prev.sortOrder;
    memberLevelStore.update(prev.id, { sortOrder: curr.sortOrder });
    memberLevelStore.update(curr.id, { sortOrder: tmp });
  },

  moveDown: (id: string) => {
    const sorted = [..._levels].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = sorted.findIndex((l) => l.id === id);
    if (idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    const curr = sorted[idx];
    const tmp = next.sortOrder;
    memberLevelStore.update(next.id, { sortOrder: curr.sortOrder });
    memberLevelStore.update(curr.id, { sortOrder: tmp });
  },

  /** 与 GET /member-levels 同步，供后台与会员申请页使用 */
  replaceFromApi(levels: MemberLevel[]): void {
    _levels.length = 0;
    for (const l of levels) {
      _levels.push({ ...l, benefitIds: l.benefitIds ? [...l.benefitIds] : [] });
    }
  },
};
